import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import AttachmentList, { LinkifiedText, PendingAttachments } from './AttachmentList';
import { FILE_ACCEPT, usePendingUploads } from './uploads';
import './Messages.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.alooma.mg';

function Messages({ userType, initialConversationId, onActivity }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const [sendError, setSendError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const uploads = usePendingUploads();
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollingRef = useRef(null);
  const listPollingRef = useRef(null);
  const socketRef = useRef(null);
  const selectedConvRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const consumedInitialRef = useRef(null);

  const currentUserId = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}').id || null;
    } catch (_) {
      return null;
    }
  })();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setConversations(data.conversations || []);
        return data.conversations || [];
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setIsLoading(false);
    }
    return [];
  }, []);

  const fetchMessages = useCallback(async (conversationId, showLoading = true) => {
    if (showLoading) setIsLoadingMessages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/conversations/${conversationId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setMessages(data.messages || []);
        setTimeout(scrollToBottom, 100);
        // Opening a thread marks messages read on the server; refresh unread
        // badges (both the parent dashboard counter and the conversation list).
        if (onActivity) onActivity();
        fetchConversations();
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [scrollToBottom, onActivity, fetchConversations]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Keep the conversation list (and unread badges) fresh even when a thread
  // isn't open. This is the polling fallback when websockets are unavailable.
  useEffect(() => {
    listPollingRef.current = setInterval(() => {
      fetchConversations();
    }, 10000);
    return () => clearInterval(listPollingRef.current);
  }, [fetchConversations]);

  // Real-time via Socket.IO (falls back gracefully to polling on failure)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = io(API_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
    });
    socketRef.current = socket;

    socket.on('message', (msg) => {
      const activeConv = selectedConvRef.current;
      if (activeConv && msg.conversationId === activeConv.id) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev; // dedupe
          return [...prev, { ...msg, isOwnMessage: msg.senderId === currentUserId }];
        });
        setIsOtherTyping(false);
        setTimeout(scrollToBottom, 80);
        // Mark as read since the thread is open
        fetchMessages(activeConv.id, false);
      } else {
        // Message for another conversation — refresh list/badges
        fetchConversations();
        if (onActivity) onActivity();
      }
    });

    socket.on('typing', ({ conversationId, userId, isTyping }) => {
      const activeConv = selectedConvRef.current;
      if (activeConv && conversationId === activeConv.id && userId !== currentUserId) {
        setIsOtherTyping(isTyping);
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [currentUserId, scrollToBottom, fetchMessages, fetchConversations, onActivity]);

  // Auto-select a conversation when deep-linked from a bid/notification.
  // Re-runs whenever the requested id changes so opening different threads works.
  useEffect(() => {
    if (!initialConversationId) return;
    if (consumedInitialRef.current === initialConversationId) return;
    if (conversations.length === 0) return;
    const match = conversations.find((c) => c.id === initialConversationId);
    if (match) {
      consumedInitialRef.current = initialConversationId;
      handleSelectConversation(match);
    }
  }, [initialConversationId, conversations]);

  useEffect(() => {
    selectedConvRef.current = selectedConversation;
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);

      // Join the socket room for this conversation
      socketRef.current?.emit('join', { conversationId: selectedConversation.id });

      // Polling fallback for the open thread (in case sockets are down)
      pollingRef.current = setInterval(() => {
        fetchMessages(selectedConversation.id, false);
      }, 5000);

      const joinedId = selectedConversation.id;
      return () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
        socketRef.current?.emit('leave', { conversationId: joinedId });
      };
    }
  }, [selectedConversation]);

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setMessages([]);
    setIsOtherTyping(false);
    setSendError('');
    uploads.reset();
    messageInputRef.current?.focus();
  };

  const emitTyping = (isTyping) => {
    if (!socketRef.current || !selectedConversation) return;
    socketRef.current.emit('typing', { conversationId: selectedConversation.id, isTyping });
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    emitTyping(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => emitTyping(false), 1500);
  };

  const canSend = (newMessage.trim() || uploads.refs.length > 0) && !uploads.isUploading && !uploads.hasErrors;

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!canSend || !selectedConversation || isSending) return;

    setIsSending(true);
    setSendError('');
    const messageText = newMessage;
    const pendingFiles = uploads.items;
    const attachments = uploads.refs;
    setNewMessage('');
    uploads.reset();
    emitTyping(false);

    // Optimistically add message (attachments show as cards until the
    // server returns signed urls)
    const tempMessage = {
      id: 'temp-' + Date.now(),
      conversationId: selectedConversation.id,
      content: messageText,
      attachments: pendingFiles.map((f) => ({
        key: `pending-${f.id}`, name: f.name, size: f.size, contentType: f.contentType, pending: true,
      })),
      isOwnMessage: true,
      createdAt: new Date().toISOString(),
      isPending: true,
    };
    setMessages(prev => [...prev, tempMessage]);
    scrollToBottom();

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/conversations/${selectedConversation.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ content: messageText, attachments }),
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        // Replace temp message with real one (unless the socket already
        // delivered it)
        setMessages(prev => (prev.some(m => m.id === data.message.id)
          ? prev.filter(m => m.id !== tempMessage.id)
          : prev.map(m => (m.id === tempMessage.id ? { ...data.message, isPending: false } : m))
        ));
        fetchConversations(); // Refresh to update last message
      } else {
        // Remove failed message and restore the draft
        setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
        setNewMessage(messageText);
        uploads.restore(pendingFiles);
        setSendError(data.message || 'Message could not be sent');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
      setNewMessage(messageText);
      uploads.restore(pendingFiles);
      setSendError('Message could not be sent. Check your connection.');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const getOtherPartyName = (conversation) => {
    return userType === 'brand' ? conversation.influencerName : conversation.brandName;
  };

  const getOtherPartyInitial = (conversation) => {
    const name = getOtherPartyName(conversation);
    return name?.charAt(0) || '?';
  };

  if (isLoading) {
    return (
      <div className="messages-loading">
        <div className="messages-loader"></div>
        <p>Loading conversations...</p>
      </div>
    );
  }

  return (
    <div className="messages-container">
      {/* Conversations Sidebar */}
      <div className={`conversations-sidebar ${selectedConversation ? 'hide-mobile' : ''}`}>
        <div className="conversations-header">
          <h2>Messages</h2>
          <span className="conversation-count">{conversations.length}</span>
        </div>
        
        {conversations.length === 0 ? (
          <div className="no-conversations">
            <div className="no-conv-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <h3>No conversations yet</h3>
            <p>When you accept or have a bid accepted, you'll be able to message here.</p>
          </div>
        ) : (
          <div className="conversations-list">
            {conversations.map((conversation) => (
              <button
                key={conversation.id}
                className={`conversation-item ${selectedConversation?.id === conversation.id ? 'active' : ''}`}
                onClick={() => handleSelectConversation(conversation)}
              >
                <div className="conv-avatar">
                  {getOtherPartyInitial(conversation)}
                  {conversation.unreadCount > 0 && (
                    <span className="conv-unread-dot">{conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}</span>
                  )}
                </div>
                <div className="conv-content">
                  <div className="conv-header">
                    <span className={`conv-name ${conversation.unreadCount > 0 ? 'has-unread' : ''}`}>
                      {getOtherPartyName(conversation)}
                    </span>
                    {conversation.lastMessageAt && (
                      <span className="conv-time">{formatTime(conversation.lastMessageAt)}</span>
                    )}
                  </div>
                  <div className="conv-preview">
                    <span className="conv-job">{conversation.jobTitle}</span>
                    {conversation.lastMessage && (
                      <span className="conv-last-message">
                        {conversation.lastMessage.substring(0, 40)}
                        {conversation.lastMessage.length > 40 ? '...' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Chat Area */}
      <div className={`chat-area ${!selectedConversation ? 'hide-mobile' : ''}`}>
        {!selectedConversation ? (
          <div className="no-chat-selected">
            <div className="no-chat-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
              </svg>
            </div>
            <h2>Select a conversation</h2>
            <p>Choose a conversation from the sidebar to start messaging</p>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="chat-header">
              <button 
                className="back-to-list"
                onClick={() => setSelectedConversation(null)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
              </button>
              <div className="chat-header-avatar">
                {getOtherPartyInitial(selectedConversation)}
              </div>
              <div className="chat-header-info">
                <h3>{getOtherPartyName(selectedConversation)}</h3>
                <span className="chat-job-ref">{selectedConversation.jobTitle}</span>
              </div>
            </div>

            {/* Messages */}
            <div className="chat-messages">
              {isLoadingMessages ? (
                <div className="messages-loading-inline">
                  <div className="messages-loader-small"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="no-messages">
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                <>
                  <div className="messages-start-notice">
                    <span>Conversation started for "{selectedConversation.jobTitle}"</span>
                  </div>
                  {messages.map((message, index) => {
                    const showTimestamp = index === 0 || 
                      new Date(message.createdAt) - new Date(messages[index - 1].createdAt) > 5 * 60 * 1000;
                    
                    return (
                      <React.Fragment key={message.id}>
                        {showTimestamp && (
                          <div className="message-timestamp">
                            {formatTime(message.createdAt)}
                          </div>
                        )}
                        <div 
                          className={`message ${message.isOwnMessage ? 'own' : 'other'} ${message.isPending ? 'pending' : ''}`}
                        >
                          <div className="message-bubble">
                            {message.content && (
                              <p><LinkifiedText text={message.content} /></p>
                            )}
                            <AttachmentList attachments={message.attachments} />
                            <span className="message-time">
                              {formatMessageTime(message.createdAt)}
                              {message.isPending && ' • Sending...'}
                            </span>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  {isOtherTyping && (
                    <div className="typing-indicator">
                      <span></span><span></span><span></span>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Message Input */}
            <form
              className={`chat-input-form ${isDragging ? 'dragging' : ''}`}
              onSubmit={handleSendMessage}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                uploads.addFiles(e.dataTransfer.files);
              }}
            >
              {sendError && <div className="chat-send-error">{sendError}</div>}
              <PendingAttachments uploads={uploads} />
              <div className="chat-input-wrapper">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={FILE_ACCEPT}
                  className="chat-file-input"
                  onChange={(e) => {
                    uploads.addFiles(e.target.files);
                    e.target.value = ''; // allow picking the same file again
                  }}
                />
                <button
                  type="button"
                  className="attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSending}
                  title="Attach photos, videos or files"
                  aria-label="Attach files"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                </button>
                <textarea
                  ref={messageInputRef}
                  value={newMessage}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  rows={1}
                  disabled={isSending}
                />
                <button 
                  type="submit" 
                  className="send-btn"
                  disabled={!canSend || isSending}
                  title={uploads.isUploading ? 'Waiting for uploads to finish' : undefined}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default Messages;
